import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useMemo } from 'react';
import Script from 'next/script';
import { getStructuredData } from '../../../data/structured';
import useNextConfig from '../../hooks/useNextConfig/useNextConfig';
import { requireFromStaticAsset } from '../../utils/assetsUtils';
import { buildCanonicalURL } from '../../utils/seo/canonical';

const SOCIAL_PREVIEW = 'default-preview-social.png';

export interface PageHeadProps {
  SEO: {
    title: string,
    description: string,
    canonical?: string,
    robots?: string[],
    next?: string,
    prev?: string,
    keywords?: string[],
  },
  type?: string,
  article?: {
    author?: string,
    section?: string,
    tags?: string[],
  },
  image?: {
    url: string,
    width?: number,
    height?: number,
  },
  structuredData?: any,
  /** error pages must not declare a canonical URL */
  withCanonical?: boolean,
}

const PageHead: FunctionComponent<PageHeadProps> = ({
  SEO,
  type,
  image,
  article,
  structuredData,
  withCanonical = true,
}) => {
  const { asPath } = useRouter();
  const { GTM } = useNextConfig();
  const pageURL = useMemo(
    () => SEO.canonical || buildCanonicalURL(asPath),
    [SEO.canonical, asPath],
  );

  return (
    <>
      <Head>
        <title key="title">{`${SEO.title} | Dove.it`}</title>
        <meta key="viewport" name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta key="description" name="description" content={SEO.description} />
        <meta key="theme-color" name="theme-color" content="#E31D93" />
        <meta key="format-detection" name="format-detection" content="telephone=no" />
        <meta key="og:title" property="og:title" content={SEO.title} />
        <meta key="og:description" property="og:description" content={SEO.description} />
        <meta key="og:type" property="og:type" content={type || 'website'} />
        <meta key="og:url" property="og:url" content={pageURL} />
        <meta key="og:image" property="og:image" content={image && image.url ? image.url : `${requireFromStaticAsset(SOCIAL_PREVIEW)}`} />
        <meta key="twitter:title" property="twitter:title" content={SEO.title} />
        <meta key="twitter:description" property="twitter:description" content={SEO.description} />
        <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
        {image && image.width && (
        <meta key="og:image:width" property="og:image:width" content={`${image.width}`} />
        )}
        {image && image.height && (
        <meta key="og:image:height" property="og:image:height" content={`${image.height}`} />
        )}
        {SEO.robots && SEO.robots.length > 0 && (
        <meta key="robots" name="robots" content={SEO.robots.join(',')} />
        )}
        {SEO.keywords && SEO.keywords.length > 0 && (
        <meta key="keywords" name="keywords" content={SEO.keywords.join(',')} />
        )}
        {article && (
        <meta key="robots:max-image-preview" name="robots" content="max-image-preview:large" />
        )}
        {article && article.author && (
        <meta key="article:author" property="article:author" content={article.author} />
        )}
        {article && article.section && (
        <meta key="article:section" property="article:section" content={article.section} />
        )}
        {article && article.tags && (
          article.tags.map((tag) => (<meta key={`article:tag:${tag}`} property="article:tag" content={tag} />))
        )}
        {withCanonical && (
        <link key="canonical-url" href={pageURL} rel="canonical" />
        )}
        {SEO.next && (
        <link key="next" href={SEO.next} rel="next" />
        )}
        {SEO.prev && (
        <link key="prev" href={SEO.prev} rel="prev" />
        )}
      </Head>
      {/** outside next/head: its head manager duplicates inline scripts on hydration */}
      <script
        key="structured-data"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData || getStructuredData()) }}
      />
      <Script
        key="GTM"
        id="GTM"
        strategy="beforeInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://${GTM.host}/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${GTM.containerId}');
          `,
        }}
      />
    </>
  );
};

export default PageHead;
